import React, { CSSProperties, ReactNode, useLayoutEffect, useRef } from 'react';
import { ButtonType } from '../../components/controls/button';
import { ModalButtonBar, ModalButtonProps } from '../../components/controls/modalbuttonbar';
import { ModalContainer } from './ModalContainer';
import { ModalContent } from './ModalContent';
import { ModalHeader } from './ModalHeader';
import { ModalFooter } from './ModalFooter';

/**
 * {@docCategory Modal}
 */
export type ModalSize = 'small' | 'medium' | 'large' | 'fullscreen';

/**
 * {@docCategory Modal}
 */
export type ModalPaddingType = boolean | 'small' | 'medium' | 'large';

/**
 * {@docCategory Modal}
 */
export interface ModalProps {
   /**
    * Specifies if the Modal is visible.
    * @defaultvalue false
    */
   visible?: boolean;

   /**
    * Sets the title of the Modal. If the title is not defined and closable is false then
    * the Modal header will not be shown.
    */
   title: ReactNode;

   /**
    * If this is set, it enables analytics pageviews for the modal.  Set this to the simple name
    * of the Modal using all lowercase with dashes to separate words.  For example:
    * approve-employee-request.
    */
   analyticsName?: string;

   /**
    * Specifies the size of the Modal.
    *
    * @default 'small'
    */
   size?: ModalSize;

   /**
    * Specifies if the Modal is closable. This includes the close button,
    * the ESC key, and the mask clicking. The developer must override the onClose
    * callback in order for the Modal to be closed.
    * @defaultvalue true
    */
   closable?: boolean;

   /**
    * Specifies if the content area of the Modal will scroll.
    * @defaultvalue true
    */
   scroll?: boolean;

   /**
    * Determines if the modal has padding around the content.
    *
    * @defaultvalue true
    */
   padding?: ModalPaddingType;

   /**
    * Used to customize the buttons in the action area of the Modal. If this is not set or is set
    * to an empty array, the Modal will have the standard OK and Cancel buttons.
    */
   buttons?: ModalButtonProps[];

   /**
    * Extra content on the left side of the button bar
    */
   buttonsLeftContent?: ReactNode;

   /**
    * Used to place a custom component in the Modal's action area. If this is specified, it will
    * override the buttons prop and the default OK/Cancel button configuration. The content will
    * be placed in a container that will ensure consistent padding and will ensure the horizontal
    * separator is shown.
    */
   footer?: ReactNode;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles for the Modal content area.
    */
   style?: CSSProperties;

   /**
    * Called after the Modal is shown.
    */
   onAfterOpen?: () => void;

   /**
    * Called after the Modal is hidden.
    */
   onAfterClose?: () => void;

   /**
    * Callback that is called when the user presses the OK button.
    */
   onOk?: () => void;

   /**
    * Callback that is called when the user presses the Cancel button,
    * closes the Modal using the close button, the ESC key, or by clicking on the mask.
    */
   onCancel?: () => void;

   /**
    * Text to override the default text for the OK button.
    * @defaultvalue 'OK'
    */
   okText?: ReactNode;

   /**
    * The type of the OK button.
    * @defaultvalue 'primary'
    */
   okType?: ButtonType | 'danger';

   /**
    * Text to override the default text for the Cancel button.
    * @defaultvalue 'Cancel'
    */
   cancelText?: ReactNode;

   /**
    * Disable the OK button
    * @defaultvalue false
    */
   disableOk?: boolean;

   /**
    * Disable the cancel button
    * @defaultvalue false
    */
   disableCancel?: boolean;

   /**
    * Hide the OK button
    * @defaultvalue false
    */
   hideOk?: boolean;

   /**
    * Hide the cancel button
    * @defaultvalue false
    */
   hideCancel?: boolean;

   /**
    * Content to be displayed in the Modal's content area.
    */
   children?: ReactNode;
}

export const Modal = (props: ModalProps) => {
   const closable = props.closable === undefined ? true : props.closable;

   const padding = props.padding === undefined ? true : props.padding;

   return (
      <ModalContainer
         visible={props.visible}
         shouldCloseOnMaskClick={false}
         shouldCloseOnEsc={closable}
         size={props.size}
         style={props.style}
         className={props.className}
         onClose={props.onCancel}
         onAfterClose={props.onAfterClose}
         onAfterOpen={props.onAfterOpen}
         analyticsName={props.analyticsName}
      >
         <ModalContent
            scroll={props.scroll}
            padding={padding}
            header={modalHeader(props.title, closable, padding, props.onCancel)}
            footer={modalFooter(props, padding)}
         >
            {props.children}
         </ModalContent>
      </ModalContainer>
   );
};

const modalHeader = (title: ReactNode, closable: boolean, padding: ModalPaddingType, onClose?: () => void) => {
   return (
      <ModalHeader
         title={title}
         padding={padding}
         onClose={closeHandler(closable, onClose)}
      />
   );
};

const closeHandler = (showCloseButton: boolean, onClose?: () => void) => {
   if (showCloseButton) {
      return () => {
         if (onClose) {
            onClose();
         }
      };
   }

   return undefined;
};

const modalFooter = (props: ModalProps, padding: ModalPaddingType) => {
   const footer = props.footer;

   if (footer !== undefined) {
      return (
         <ModalFooter padding={padding}>
            {footer}
         </ModalFooter>
      );
   }

   return (
      <ModalFooter padding={padding}>
         <ModalButtonBar
            okText={props.okText}
            okType={props.okType}
            cancelText={props.cancelText}
            onOk={props.onOk}
            onCancel={props.onCancel}
            buttons={props.buttons}
            disableOk={props.disableOk}
            disableCancel={props.disableCancel}
            hideOk={props.hideOk}
            hideCancel={props.hideCancel}
            leftContent={props.buttonsLeftContent}
         />
      </ModalFooter>
   );
};
